import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { apiError } from "@/lib/apiError";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { Brand, LangToggle, inputClass, btnPrimary, btnGhost } from "@/components/Brand";

export default function Login() {
  const { login, signupStudio } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ studio_name: "", name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = mode === "login" ? await login(form.email, form.password) : await signupStudio(form);
      navigate(data.platform_role === "platform_owner" ? "/platform" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(apiError(err, "Something went wrong"));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#050505] text-[#F3F3F1] grain flex" data-testid="login-page">
      <div className="hidden lg:block w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1519741196428-6a2175fa2557?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9vZHklMjB3ZWRkaW5nJTIwcGhvdG9ncmFwaHl8ZW58MHx8fHwxNzg3NTQ5ODA1fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#050505]" />
        <div className="absolute top-8 left-10">
          <Brand />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-sm fade-up">
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <Brand />
            <LangToggle />
          </div>
          <p className="eyebrow mb-4">{mode === "login" ? t("studio_access") : t("new_studio")}</p>
          <h1 className="text-4xl tracking-tighter font-medium mb-10">
            {mode === "login" ? t("login_title") : t("signup_title")}
          </h1>

          <button data-testid="google-login-btn" onClick={googleLogin} className={`${btnGhost} w-full py-3.5`}>
            {t("continue_google")}
          </button>

          <div className="flex items-center gap-4 my-8">
            <span className="h-px flex-1 bg-[#2A2A2A]" />
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#707070]">{t("or_email")}</span>
            <span className="h-px flex-1 bg-[#2A2A2A]" />
          </div>

          <form onSubmit={submit} className="space-y-6">
            {mode === "signup" && (
              <>
                <input data-testid="studio-name-input" className={inputClass} placeholder={t("studio_name")}
                  value={form.studio_name} onChange={(e) => setForm({ ...form, studio_name: e.target.value })} required />
                <input data-testid="signup-name-input" className={inputClass} placeholder={t("your_name")}
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              </>
            )}
            <input data-testid="login-email-input" type="email" className={inputClass} placeholder={t("email")}
              value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
            <input data-testid="login-password-input" type="password" className={inputClass} placeholder={t("password")}
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required />
            <button data-testid="login-submit-btn" disabled={busy} className={`${btnPrimary} w-full py-4`}>
              {busy && <Loader2 size={14} className="animate-spin" />}
              {mode === "login" ? t("enter_studio") : t("create_studio")}
            </button>
          </form>

          <div className="mt-8 flex flex-col gap-1">
            <button data-testid="toggle-auth-mode-btn" onClick={() => setMode(mode === "login" ? "signup" : "login")}
              className="inline-flex items-center min-h-[44px] text-xs text-[#A8A8A8] hover:text-[#D4AF37] transition-colors text-left">
              {mode === "login" ? t("to_signup") : t("to_login")}
            </button>
            <Link data-testid="to-client-login-link" to="/client-login"
              className="inline-flex items-center min-h-[44px] text-xs text-[#707070] hover:text-[#D4AF37]">
              {t("nav_client")} →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
